<template>
  <div v-if="item.checklists" class="mt-4 mb-3">
    <div v-for="(checklist, index) in filteredChecklists" :key="checklist.id" class="mb-3 mt-1">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <div class="label" style="color: black !important; font-weight: bold">
          {{ checklist.title }}
        </div>

        <div>
          <button class="btn btn-xs btn-info mr-2" v-if="checklist.sortOrder !== 1 && !readOnly" @click="changeSortOrder(-1, checklist.id)">
            <i class="far fa-arrow-alt-up"></i>
          </button>
          <button
            class="btn btn-xs btn-info mr-2"
            v-if="checklist.sortOrder !== filteredChecklists.length && !readOnly"
            @click="changeSortOrder(1, checklist.id)"
          >
            <i class="far fa-arrow-alt-down"></i>
          </button>
          <button class="btn btn-xs btn-info mr-2" :id="'renameChecklist' + checklist.id" v-if="!readOnly">
            <i class="far fa-pencil-alt"></i>
          </button>
          <b-popover :target="'renameChecklist' + checklist.id" :ref="'renameChecklist' + checklist.id" triggers="click blur">
            <input type="text" class="form-control" v-model="checklist.title" />
            <button class="mt-2 btn btn-block btn-success" @click="renameChecklist(checklist.id, checklist.title)">Save</button>
          </b-popover>

          <button class="btn btn-xs btn-info" @click="deleteChecklist(checklist.id)" v-if="!readOnly">
            <i class="far fa-trash"></i>
          </button>
        </div>
      </div>

      <div v-if="checklist.items.length" class="progress mb-3">
        <div
          class="progress-bar"
          role="progressbar"
          :style="{ width: getFinishedPercentage(checklist.items) }"
          :aria-valuenow="getFinishedCount(checklist.items)"
          aria-valuemin="0"
          :aria-valuemax="checklist.items.length"
        >
          {{ getFinishedPercentage(checklist.items) }}
        </div>
      </div>

      <div>
        <div v-for="(checklistItem, checklistItemIndex) in filteredChecklistItems(checklist.items)" :key="checklistItem.id" class="mb-2">
          <ChecklistItem
            :readOnly="readOnly"
            :item="checklistItem"
            :itemIndex="checklistItemIndex"
            :boardItemId="item.id"
            :itemcount="filteredChecklistItems(checklist.items).length"
            :isTemplate="isTemplate"
          ></ChecklistItem>
        </div>
      </div>

      <div class="mt-1 mb-1">
        <div v-if="newItemState.checklistId === checklist.id">
          <form class="edit-form" @submit.prevent="saveNewItem">
            <div class="label mt-1">Title</div>
            <input type="text" class="form-control form-control-sm" v-model="newItemState.title" ref="title" />

            <div class="label mt-2">Description</div>
            <textarea-autosize rows="4" class="form-control form-control-sm" v-model="newItemState.description" />
            <div class="d-flex justify-content-between mt-2">
              <button type="submit" class="btn btn-sm btn-success" style="width: 60px" :disabled="!newItemState.title">Save</button>

              <div @click="cancelAddNewItem()">
                <i class="far fa-trash-alt text-danger cursor-pointer"></i>
              </div>
            </div>
          </form>
        </div>
        <div v-else>
          <div v-if="checklist.id !== newItemState.id && !readOnly" class="btn btn-sm btn-info" @click="addNewItem(checklist.id)">
            Add item
          </div>
        </div>
      </div>

      <hr v-if="index !== item.checklists.length - 1" />
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { sortBy } from 'lodash';
  import { ChecklistModule, ChecklistStore } from '@/store/checklist';
  import { ConfirmModal } from '@/helpers/confirm-modal';
  import { BoardItemDto, ChecklistItemDto } from '@/apiclient/apiclient_generated';
  import { Prop } from 'vue-property-decorator';
  import ChecklistItem from './ChecklistItem.vue';
  import { GlobalHelper } from '@/helpers/global-helper';

  @Component({
    components: {
      ChecklistItem,
    },
  })
  export default class Checklist extends Vue {
    private checklistStore: ChecklistModule = ChecklistStore;
    private newItemState: ChecklistItemDto = { isEditable: true } as ChecklistItemDto;
    @Prop() item: BoardItemDto;
    @Prop({ default: false }) isTemplate: boolean;
    @Prop() readOnly: boolean;

    get filteredChecklists() {
      return sortBy(this.item.checklists, 'sortOrder');
    }

    filteredChecklistItems(items) {
      return sortBy(items, 'sortOrder', 'updatedDate');
    }

    mounted() {
      this.resetNewItemState();
    }

    getFinishedPercentage(items: ChecklistItemDto[]) {
      const closedCount = items.filter((_) => _.isCompleted).length;
      return Math.min(Math.max(Math.floor((closedCount / items.length) * 100), 0), 100) + '%';
    }

    getFinishedCount(items: ChecklistItemDto[]) {
      const result = items.filter((_) => _.isCompleted).length;
      return result;
    }

    async deleteChecklist(checklistId) {
      const boardItemId = this.item.id;

      const success = () => {
        this.checklistStore.deleteChecklist({ checklistId, boardItemId });
      };

      ConfirmModal.confirm(
        this,
        `Are you sure you want to delete this checklist with all items?`,
        'Confirm delete',
        'Yes, delete',
        'No',
        success,
        null
      );
    }

    async addNewItem(checklistId) {
      this.newItemState.checklistId = checklistId;
      await this.$nextTick();
      (this.$refs.title as HTMLInputElement[])[0].focus();
    }

    async saveNewItem() {
      const params = {
        boardItemId: this.item.id,
        item: {
          title: this.newItemState.title,
          description: this.newItemState.description,
          checklistId: this.newItemState.checklistId,
        } as ChecklistItemDto,
      };

      await this.checklistStore.addChecklistItem(params);
      this.cancelAddNewItem();
    }

    async renameChecklist(checklistId: string, newTitle: string) {
      const params = {
        boardItemId: this.item.id,
        checklistId: checklistId,
        title: newTitle,
      };
      await this.checklistStore.renameChecklist(params);
      this.$root.$emit('bv::hide::popover');
      GlobalHelper.NotifySuccess('Checklist renamed');
    }

    cancelAddNewItem() {
      this.resetNewItemState();
    }

    async changeSortOrder(direction: number, checklistId: string) {
      const params = {
        boardItemId: this.item.id,
        checklistId: checklistId,
        direction: direction,
      };
      await this.checklistStore.changeSortOrder(params);
    }

    resetNewItemState() {
      this.newItemState = {
        title: '',
        checklistId: '',
        isEditable: true,
      } as ChecklistItemDto;
    }
  }
</script>

<style scoped lang="scss">
  .mb-0 {
    margin-bottom: 0 !important;
  }

  .fa-trash-alt {
    font-size: 1.3em;
    margin-top: 3px;
  }

  .edit-form {
    padding: 8px;
    background: lighten(#eaeaea, 2%);
    margin-bottom: 4px;
    border-radius: 4px;
  }
</style>
